import React, { useMemo, useState } from 'react';
import { Box, Chip, Group, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  CardSize,
  Competency,
  CompetencyMapData,
  Swimlane,
  User,
  UserCompetency,
} from '../../types';
import { CompetencyCard } from './CompetencyCard';
import { CompetencyDrawer } from './CompetencyDrawer';

interface Props {
  data: CompetencyMapData;
  competencyMapId?: string;
  cardSize: CardSize;
  userCompetencies?: UserCompetency[];
  currentUser?: User;
}

const MIN_WIDTH: Record<CardSize, number> = { S: 200, M: 240, L: 280 };
const ALL = 'all';

// Вид «Карточки»: карта как список для чтения — грейд за грейдом, без горизонтальной
// прокрутки таблицы. Направление, которое в таблице задаёт колонка, здесь — фильтр
// сверху и подзаголовок группы внутри грейда. Правка карты остаётся в таблице.
export const CompetencyMapCards: React.FC<Props> = ({
  data,
  competencyMapId,
  cardSize,
  userCompetencies = [],
  currentUser,
}) => {
  const { t } = useTranslation();
  const [lane, setLane] = useState<string>(ALL);
  const [drawer, setDrawer] = useState<{
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  } | null>(null);

  const laneIds = new Set(data.swimlanes.map((s) => s._id));
  const owned = useMemo(
    () => new Map(userCompetencies.map((uc) => [uc.competencyId, uc])),
    [userCompetencies],
  );

  const sections = data.grades
    .map((grade, gradeIdx) => ({
      grade,
      gradeIdx,
      comps: (grade.competencies ?? [])
        .filter((c) => laneIds.has(c.swimlaneId) && (lane === ALL || c.swimlaneId === lane))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    }))
    .filter((s) => s.comps.length > 0);

  return (
    <Stack gap="lg">
      {data.swimlanes.length > 1 && (
        <Chip.Group multiple={false} value={lane} onChange={(v) => setLane(v as string)}>
          <Group gap="xs" role="group" aria-label={t('competencyMaps.cards.filter')}>
            <Chip value={ALL} size="sm" variant="light">
              {t('competencyMaps.cards.allLanes')}
            </Chip>
            {/* Длинное направление переносится: в одну строку чип на 375px давал прокрутку вбок. */}
            {data.swimlanes.map((s) => (
              <Chip
                key={s._id}
                value={s._id}
                size="sm"
                variant="light"
                style={{ maxWidth: '100%' }}
                styles={{
                  label: {
                    maxWidth: '100%',
                    height: 'auto',
                    minHeight: 'var(--chip-size)',
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                    paddingBlock: 4,
                  },
                }}
              >
                {s.title}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      )}

      {sections.length === 0 && (
        <Text c="dimmed" size="sm">
          {lane === ALL ? t('competencyMaps.cards.emptyMap') : t('competencyMaps.cards.empty')}
        </Text>
      )}

      {sections.map(({ grade, gradeIdx, comps }) => (
        <Box component="section" key={grade._id} aria-labelledby={`grade-${grade._id}`}>
          {/* Заголовок грейда прилипает: на длинной карте видно, в каком ты грейде. */}
          <Group
            gap="xs"
            align="baseline"
            py="xs"
            style={{
              position: 'sticky',
              top: 'var(--app-shell-header-offset, 0px)',
              zIndex: 3,
              background: 'var(--mantine-color-body)',
            }}
          >
            <Title order={4} id={`grade-${grade._id}`}>
              {grade.title}
            </Title>
            <Text size="sm" c="dimmed">
              {t('competencyMaps.cards.count', { count: comps.length })}
            </Text>
          </Group>
          {/* Направление при «Все» — подзаголовок группы, а не подпись на каждой карточке. */}
          <Stack gap="sm" mt="xs">
            {data.swimlanes
              .map((swimlane) => ({
                swimlane,
                laneComps: comps.filter((c) => c.swimlaneId === swimlane._id),
              }))
              .filter((g) => g.laneComps.length > 0)
              .map(({ swimlane, laneComps }) => (
                <Box key={swimlane._id}>
                  {lane === ALL && (
                    <Text size="xs" c="dimmed" mb={6}>
                      {swimlane.title}
                    </Text>
                  )}
                  <Box
                    style={{
                      display: 'grid',
                      gap: 'var(--mantine-spacing-sm)',
                      gridTemplateColumns: `repeat(auto-fill, minmax(min(${MIN_WIDTH[cardSize]}px, 100%), 1fr))`,
                      alignItems: 'start',
                    }}
                  >
                    {laneComps.map((comp) => (
                      <CompetencyCard
                        key={comp._id}
                        comp={comp}
                        gradeIdx={gradeIdx}
                        swimlane={swimlane}
                        onClick={() => setDrawer({ comp, gradeIdx, swimlane })}
                        dragProps={{}}
                        dragHandleProps={{}}
                        isDragging={false}
                        isDragDropEnabled={false}
                        userHas={owned.has(comp._id)}
                        userCompetency={owned.get(comp._id)}
                        cardSize={cardSize}
                        isRequired={comp.isRequired}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
          </Stack>
        </Box>
      ))}

      <CompetencyDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        grades={data.grades.map((g) => g.title)}
        userCompetencies={userCompetencies}
        competencyMapId={competencyMapId}
        currentUser={currentUser}
      />
    </Stack>
  );
};
